import {
    getNodeId,
    isResponseCode5xx,
    logOperationalMetric,
    chainWalk,
    convertTableJsonToAsset,
    setAliasToRequest,
    checkInactiveOrDeprecatedAsset,
    getAliasFromParent,
    getAssetLabelFromMac,
    updateConfigurationsInAssetObject,
    deepCopyObject
} from "../../../util/Util";
import {ACTIVE, DEPRECATED, VAPR_ASSET_TYPE, DEFAULT_VENDOR_ATTRIBUTES, LOADING_STRING,
    ALERT_UPDATE_ASSET_MESSAGE, ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET,
    ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, ALERT_CREATE_SUCCESS_ASSET_MESSAGE,
    ALERT_ASSET_STATUS_MESSAGE,
    ALERT_DELETE_ASSET_MESSAGE, ALERT_DELETE_SUCCESS_ASSET_MESSAGE,
    MERIDIAN_ALERT_TYPE, VAPR_MODES, VAPR_DEFAULT_MODE} from "../../../util/Constants";
import {isValidEntry, PATTERNS} from "../../../util/Validations";
import {getAssets, addAsset, updateAsset, setAssetStatus, getAssetByAssetLabel, updateAssetInPlace} from "../../../resources/AssetResource";

const metricProcessName = "VAPR_DEVICE_DETAILS";

// Normalizes the stored desiredMode the same way the edge does (SVAS VaprAssetMapper.parseMode: case-insensitive,
// "boot"/"boot_mode" → BOOT_MODE), so the UI shows the mode the camera actually runs. Anything else, including blank,
// is NORMAL_MODE, which is what the edge applies by default.
export const parseVaprMode = (desiredMode) => {
    const token = String(desiredMode || "").trim().toUpperCase();
    return (token === "BOOT" || token === VAPR_MODES.BOOT_MODE.value) ? VAPR_MODES.BOOT_MODE.value : VAPR_DEFAULT_MODE;
};

export function fetchVaprDeviceDetails(dispatcher) {
    const request = {
        nodeId: getNodeId(),
        assetType: VAPR_ASSET_TYPE
    }
    return getVaprDeviceDetails(dispatcher, request)
}

async function getVaprDeviceDetails(dispatcher, request) {
    const model = {
        vaprDeviceInformation: {},
        macAddressArray: [],
        locationConfig: {}
    }
    let pageToken = null;
    do {
        const startTime = Date.now();
        const getAssetsResult = await getAssets(request.nodeId, request.assetType, pageToken);
        const isSuccess = getAssetsResult.status === 200;
        const isFault = isResponseCode5xx(getAssetsResult.status);
        logOperationalMetric(startTime, metricProcessName, "getVaprDeviceDetails", isSuccess, isFault);

        if (isSuccess) {
            addVaprDeviceDetailsToModel(getAssetsResult, model);
            pageToken = getAssetsResult.data.pageToken;
        } else {
            dispatcher({type: "RECEIVE_DATA", data: model});
            return;
        }
    } while (pageToken);
    dispatcher({type: "RECEIVE_DATA", data: model});
}

const addVaprDeviceDetailsToModel = (result, model) => {
    if ("data" in result && "assetDetailsList" in result.data) {
        result.data.assetDetailsList.forEach((deviceDetail) => {
            if (deviceDetail.assetStatus === ACTIVE) {
                const mac = deviceDetail.assetAttributes.mac;
                const deviceConfig = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.deviceConfig), {});
                const detectionFov = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.detectionFov), {});
                const projectionFov = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.projectionFov), {});
                const monitoring = chainWalk(() => JSON.parse(deviceDetail.assetAttributes.monitoring), {});
                const rawSceneBrightnessLevel = chainWalk(() => deviceDetail.assetAttributes.sceneBrightnessLevel, "");
                model.macAddressArray.push(mac);
                model.vaprDeviceInformation[mac] = {
                    name: deviceDetail.assetAttributes.name,
                    mac: mac,
                    ip: deviceDetail.assetAttributes.ip,
                    port: deviceDetail.assetAttributes.port,
                    deviceId: deviceConfig.id || "",
                    deviceType: chainWalk(() => deviceDetail.assetAttributes.deviceType, ""),
                    detectionFovLength: detectionFov.length || "",
                    detectionFovBreadth: detectionFov.breadth || "",
                    detectionFovUnit: detectionFov.unit || "mm",
                    projectionFovLength: projectionFov.length || "",
                    projectionFovBreadth: projectionFov.breadth || "",
                    projectionFovUnit: projectionFov.unit || "mm",
                    monitoringStartTime: monitoring.startTime || "0",
                    monitoringEndTime: monitoring.endTime || "0",
                    sceneBrightnessLevel: isValidEntry(rawSceneBrightnessLevel, PATTERNS.VAPR_SCENE_BRIGHTNESS_LEVEL)
                        ? rawSceneBrightnessLevel : "",
                    desiredMode: parseVaprMode(deviceDetail.assetAttributes.desiredMode),
                    vendorAttributes: deviceDetail.vendorAttributes || DEFAULT_VENDOR_ATTRIBUTES,
                    connectionStatus: LOADING_STRING,
                }
                // locationMapping is the edge-read attribute (VaprAssetMapper.parseLocationMapping).
                model.locationConfig[mac] = chainWalk(() =>
                    JSON.parse(deviceDetail.assetAttributes.locationMapping), []);
            }
        });
    }
    return model;
}

export function setVaprDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    stateValues.mac = stateValues.mac.toLowerCase();
    const request = convertTableJsonToAsset(stateValues, VAPR_ASSET_TYPE, ACTIVE);
    return addVaprDevice(dispatcher, request, showCustomAlert);
}

async function addVaprDevice(dispatcher, request, showCustomAlert) {
    const startTime = Date.now();
    await setAliasToRequest(request);
    const result = await addAsset(request);
    if (result.status === 200) {
        showCustomAlert(ALERT_CREATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
        logOperationalMetric(startTime, metricProcessName, "addVaprDevice", true, false);
    } else {
        const isFault = isResponseCode5xx(result.status);
        const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
        if (!checkInactiveOrDeprecatedAsset(chainWalk(() => getAssetResult.data.asset.assetDetails.assetStatus, null))) {
            showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE_FOR_ACTIVE_ASSET, MERIDIAN_ALERT_TYPE.ERROR);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isFault);
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        const responseSetAssetStatus = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
        if (responseSetAssetStatus.status !== 200) {
            showCustomAlert(ALERT_ASSET_STATUS_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isResponseCode5xx(responseSetAssetStatus.status));
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        const responseUpdateAsset = await updateAsset(request, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));
        if (responseUpdateAsset.status !== 200) {
            showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
            logOperationalMetric(startTime, metricProcessName, "addVaprDevice", false, isResponseCode5xx(responseUpdateAsset.status));
            fetchVaprDeviceDetails(dispatcher);
            return;
        }
        // Re-added a previously deleted (inactive/deprecated) device.
        showCustomAlert(ALERT_CREATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
        logOperationalMetric(startTime, metricProcessName, "addVaprDevice", true, false);
    }
    fetchVaprDeviceDetails(dispatcher);
}

export function updateVaprDeviceDetails(dispatcher, stateValues, showCustomAlert) {
    stateValues.mac = stateValues.mac.toLowerCase();
    const request = convertTableJsonToAsset(stateValues, VAPR_ASSET_TYPE, ACTIVE);
    return updateVaprDevice(dispatcher, request, showCustomAlert);
}

async function updateVaprDevice(dispatcher, request, showCustomAlert) {
    const startTime = Date.now();
    await setAliasToRequest(request);
    const getAssetResult = await getAssetByAssetLabel(request.assetDetails.assetLabel);
    const creationTime = chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null);
    if (!creationTime) {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
        logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", false, true);
        fetchVaprDeviceDetails(dispatcher);
        return;
    }
    const setAssetStatusResponse = await setAssetStatus(request.assetDetails.assetLabel, ACTIVE);
    if (setAssetStatusResponse.status !== 200) {
        showCustomAlert(ALERT_ASSET_STATUS_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
        logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", false, isResponseCode5xx(setAssetStatusResponse.status));
        fetchVaprDeviceDetails(dispatcher);
        return;
    }
    const updateAssetResponse = await updateAsset(request, creationTime);
    const isSuccess = updateAssetResponse.status === 200;
    const isFault = isResponseCode5xx(updateAssetResponse.status);
    if (isSuccess) {
        showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
    } else {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
    }
    logOperationalMetric(startTime, metricProcessName, "updateVaprDevice", isSuccess, isFault);
    fetchVaprDeviceDetails(dispatcher);
}

// Deploys the VAPR location configuration: fetch the asset, overlay only the `locationMapping`
// attribute (preserving all others), and persist. Dispatched via UPDATE_VAPR_CONFIGURATION.
export async function updateVaprConfiguration(dispatcher, configurations, mac, showCustomAlert) {
    const startTime = Date.now();
    const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));
    const asset = chainWalk(() => getAssetResult.data.asset, null);
    const creationTime = chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null);
    if (!asset || !creationTime) {
        showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
        logOperationalMetric(startTime, metricProcessName, "updateVaprConfiguration", false, true);
        fetchVaprDeviceDetails(dispatcher);
        return;
    }
    const alias = await getAliasFromParent();
    // locationMapping is the attribute the edge reads (VaprAssetMapper.parseLocationMapping).
    const updatedAsset = updateConfigurationsInAssetObject(
        asset, {locationMapping: configurations.locationConfig}, alias);
    const updateResult = await updateAsset(updatedAsset, creationTime);
    const isUpdateSuccess = updateResult.status === 200;
    const isFault = isResponseCode5xx(updateResult.status);
    isUpdateSuccess ? showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE) : showCustomAlert(ALERT_UPDATE_ASSET_MESSAGE);
    logOperationalMetric(startTime, metricProcessName, "updateVaprConfiguration", isUpdateSuccess, isFault);
    fetchVaprDeviceDetails(dispatcher);
}

// Deletes a VAPR device by marking its asset DEPRECATED, then refreshes the list.
// Dispatched via DELETE_DEVICE_DATA from the shared DeleteSingleDeviceModal.
export async function deleteVaprDeviceDetails(dispatcher, mac, showCustomAlert) {
    const startTime = Date.now();
    try {
        const result = await setAssetStatus(getAssetLabelFromMac(mac), DEPRECATED);
        const isSuccess = result.status === 200;
        logOperationalMetric(startTime, metricProcessName, "deleteVaprDeviceDetails", isSuccess, isResponseCode5xx(result.status));
        if (isSuccess) {
            showCustomAlert(ALERT_DELETE_SUCCESS_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.SUCCESS);
        } else {
            showCustomAlert(chainWalk(() => JSON.parse(result.errorResponse.errorMessage).message, ALERT_DELETE_ASSET_MESSAGE),
                MERIDIAN_ALERT_TYPE.ERROR);
        }
    } catch (e) {
        logOperationalMetric(startTime, metricProcessName, "deleteVaprDeviceDetails", false, true);
        showCustomAlert(ALERT_DELETE_ASSET_MESSAGE, MERIDIAN_ALERT_TYPE.ERROR);
    }
    fetchVaprDeviceDetails(dispatcher);
}

// Backend error message from a failed NARC response, falling back to the generic update-failed
// message when there is none (same pattern as Lidar/LEDController), so the operator can see why.
const toUpdateErrorMessage = (response) =>
    chainWalk(() => JSON.parse(response.errorResponse.errorMessage).message, ALERT_UPDATE_ASSET_MESSAGE);

// Saves only sceneBrightnessLevel ("0"–"10") in place, then refetches. Called by the Adjust
// Brightness modal; returns {isSuccess, errorMessage} so it can show the matching toast.
export async function updateVaprBrightnessDetails(dispatcher, level, mac) {
    const startTime = Date.now();
    // Guard: never send a level outside 0–10 (inclusive) to the backend. The modal clamps input,
    // so this is a defensive check; the caller shows a failure toast.
    if (!isValidEntry(String(level), PATTERNS.VAPR_SCENE_BRIGHTNESS_LEVEL)) {
        logOperationalMetric(startTime, metricProcessName, "updateVaprBrightnessDetails", false, false);
        return {isSuccess: false, errorMessage: ALERT_UPDATE_ASSET_MESSAGE};
    }
    const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));
    const asset = chainWalk(() => getAssetResult.data.asset, null);
    if (!asset) {
        logOperationalMetric(startTime, metricProcessName, "updateVaprBrightnessDetails", false, true);
        fetchVaprDeviceDetails(dispatcher);
        return {isSuccess: false, errorMessage: toUpdateErrorMessage(getAssetResult)};
    }
    const updatedAsset = deepCopyObject(asset);
    updatedAsset.assetDetails.assetAttributes.sceneBrightnessLevel = String(level);
    const updateAssetInPlaceResponse = await updateAssetInPlace(updatedAsset,
        chainWalk(() => asset.assetDetails.lastUpdateTime, null));
    const isSuccess = updateAssetInPlaceResponse.status === 200;
    const isFault = isResponseCode5xx(updateAssetInPlaceResponse.status);
    logOperationalMetric(startTime, metricProcessName, "updateVaprBrightnessDetails", isSuccess, isFault);
    fetchVaprDeviceDetails(dispatcher);
    // Returned so the caller (Adjust Brightness modal) can surface a success/failure toast.
    return {isSuccess, errorMessage: isSuccess ? null : toUpdateErrorMessage(updateAssetInPlaceResponse)};
}

// Saves only desiredMode (BOOT_MODE / NORMAL_MODE) in place, then refreshes. Called by the Change Mode modal;
// returns {isSuccess, errorMessage} so it can show the matching toast. On failure errorMessage is the backend's
// message when there is one (same pattern as Lidar/LEDController), otherwise the generic update-failed message.
export async function updateVaprModeDetails(dispatcher, mode, mac) {
    const startTime = Date.now();
    // Guard: only the two supported modes ever reach the backend.
    if (!Object.values(VAPR_MODES).some(supported => supported.value === mode)) {
        logOperationalMetric(startTime, metricProcessName, "updateVaprModeDetails", false, false);
        return {isSuccess: false, errorMessage: ALERT_UPDATE_ASSET_MESSAGE};
    }
    try {
        const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));
        const asset = chainWalk(() => getAssetResult.data.asset, null);
        if (!asset) {
            logOperationalMetric(startTime, metricProcessName, "updateVaprModeDetails", false, true);
            fetchVaprDeviceDetails(dispatcher);
            return {isSuccess: false, errorMessage: toUpdateErrorMessage(getAssetResult)};
        }
        const updatedAsset = deepCopyObject(asset);
        updatedAsset.assetDetails.assetAttributes.desiredMode = mode;
        const result = await updateAssetInPlace(updatedAsset, chainWalk(() => asset.assetDetails.lastUpdateTime, null));
        const isSuccess = result.status === 200;
        logOperationalMetric(startTime, metricProcessName, "updateVaprModeDetails", isSuccess, isResponseCode5xx(result.status));
        fetchVaprDeviceDetails(dispatcher);
        return {isSuccess, errorMessage: isSuccess ? null : toUpdateErrorMessage(result)};
    } catch (e) {
        logOperationalMetric(startTime, metricProcessName, "updateVaprModeDetails", false, true);
        fetchVaprDeviceDetails(dispatcher);
        return {isSuccess: false, errorMessage: ALERT_UPDATE_ASSET_MESSAGE};
    }
}



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/network/VaprDeviceNetworkAPI.js